import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";

interface TutorialPromptModalProps {
  open: boolean;
  sessionType: "solo" | "group";
  onYes: () => void;
  onNo: () => void;
  onDismissForever: () => void;
}

const TutorialPromptModal = ({ open, sessionType, onYes, onNo, onDismissForever }: TutorialPromptModalProps) => {
  const { t } = useTranslation();
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleNo = () => {
    if (dontShowAgain) onDismissForever();
    onNo();
  };

  const handleYes = () => {
    if (dontShowAgain) onDismissForever();
    onYes();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="w-full max-w-sm bg-card border border-border rounded-2xl p-6 shadow-card space-y-5"
          >
            <div className="text-center space-y-2">
              <span className="text-4xl">{sessionType === "solo" ? "🍿" : "👥"}</span>
              <h3 className="font-display text-lg font-bold text-foreground">
                {t("tutorialPrompt.title", { type: sessionType === "solo" ? t("discover.soloSession") : t("discover.groupSession") })}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("tutorialPrompt.description", { type: sessionType === "solo" ? t("discover.soloSession").toLowerCase() : t("discover.groupSession").toLowerCase() })}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleNo}
                className="flex-1 py-2.5 rounded-xl border-2 border-border bg-secondary text-sm font-medium text-foreground hover:bg-secondary/80 active:scale-95 transition-all"
              >
                {t("tutorialPrompt.noThanks")}
              </button>
              <button
                onClick={handleYes}
                className="flex-1 py-2.5 rounded-xl bg-gradient-iris text-primary-foreground text-sm font-semibold shadow-iris active:scale-95 transition-transform"
              >
                {t("tutorialPrompt.showMe")}
              </button>
            </div>

            <label className="flex items-center justify-center gap-2.5 cursor-pointer pt-1">
              <Checkbox
                checked={dontShowAgain}
                onCheckedChange={(v) => setDontShowAgain(!!v)}
              />
              <span className="text-xs text-muted-foreground leading-tight">
                {t("tutorialPrompt.dontShowAgain")}
              </span>
            </label>

            <p className="text-[10px] text-muted-foreground/60 text-center">
              {t("tutorialPrompt.reEnableHint")}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TutorialPromptModal;
